import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

export type ThemeMode = 'light' | 'dark' | 'orange';

interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) {
      return savedMode as ThemeMode;
    }
    
    // If no saved theme, detect system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't manually set a theme preference
      const savedMode = localStorage.getItem('themeMode');
      if (!savedMode) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  const getTheme = (mode: ThemeMode) => {
    switch (mode) {
      case 'light':
        return createTheme({
          palette: {
            mode: 'light',
            primary: {
              main: '#1976d2',
            },
            secondary: {
              main: '#dc004e',
            },
          },
        });
      case 'dark':
        return createTheme({
          palette: {
            mode: 'dark',
            primary: {
              main: '#90caf9',
            },
            secondary: {
              main: '#f48fb1',
            },
          },
        });
      case 'orange':
        return createTheme({
          palette: {
            mode: 'light',
            primary: {
              main: '#ff9800',
            },
            secondary: {
              main: '#ff5722',
            },
            background: {
              default: '#fff3e0',
              paper: '#ffe0b2',
            },
          },
        });
      default:
        return createTheme({
          palette: {
            mode: 'light',
            primary: {
              main: '#1976d2',
            },
            secondary: {
              main: '#dc004e',
            },
          },
        });
    }
  };

  const theme = getTheme(mode);

  return (
    <ThemeContext.Provider value={{ mode, setMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 